import { yupResolver } from '@hookform/resolvers/yup';
import { sendGTMEvent } from '@next/third-parties/google';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { Trans, useTranslation } from 'next-i18next';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import Button from '@/common/button';
import { AuthLoadingOverlay, SsoProviders } from '@/components/modals/sso';
import type { AlertStateOrEmpty } from '@/components/ui/alert';
import { Alert } from '@/components/ui/alert';
import CaptchaProvider from '@/components/ui/captcha-provider';
import type { CreateUserTrialParams } from '@/context/types';
import { useAuth } from '@/hooks/use-auth';
import { apiErrorMessageKey, TRIAL_STATUS_KEYS } from '@/utils/api-error-i18n';
import { personNameSchema } from '@/utils/person-name';
import { getUtmData } from '@/utils/user-tracking-data';

import { AlreadyLoggedInNotice } from './already-logged-in-notice';

interface FormData {
  name: string;
  email: string;
  repeatEmail: string;
  password: string;
  passwordRepeat: string;
  recaptcha: string;
}

interface SignUpCustomTrialProps {
  daysTrial: number;
  trialConfigId: number | null;
  trialPageId: number | null;
  className?: string;
  showLogo?: boolean;
  isDisabledButton?: boolean;
}

function SignUpCustomTrial({ daysTrial, trialConfigId, className = '', trialPageId, showLogo = false, isDisabledButton = false }: SignUpCustomTrialProps) {
  const { t } = useTranslation('components/trial/sign-up-custom-trial');
  const auth = useAuth();
  const { user } = auth;
  const router = useRouter();
  const [loading, setIsLoading] = useState(false);
  const [popUp, setPopUp] = useState<{
    show: boolean;
    message: string;
    state: AlertStateOrEmpty;
  }>({ show: false, message: '', state: '' });

  const schema = yup.object().shape({
    name: personNameSchema(t('form.errors.mensage_error_empty_name'), t('form.errors.mensage_error_invalid_name')),
    email: yup.string().email(t('form.errors.mensage_error_valid_email')).required(t('form.errors.mensage_error_empty_email')).max(255),
    repeatEmail: yup
      .string()
      .email(t('form.errors.mensage_error_valid_email'))
      .required(t('form.errors.mensage_error_empty_email'))
      .max(255)
      .oneOf([yup.ref('email')], t('form.errors.mensage_error_repeat_email')),
    password: yup.string().required(t('form.errors.mensage_error_empty_password')).min(6, t('form.errors.mensage_error_short_password')),
    passwordRepeat: yup
      .string()
      .required(t('form.errors.mensage_error_empty_password'))
      .min(6, t('form.errors.mensage_error_short_password'))
      .oneOf([yup.ref('password')], t('form.errors.mensage_error_repeat_password')),
    recaptcha: yup.string().required(t('form.errors.mensage_error_empty_recaptcha')),
  });

  async function handleLogin({ email, password, recaptcha }: { email: string; password: string; recaptcha: string }) {
    // Awaited, not raced: a logout landing after this login would end the new session. Its result does not
    // gate the login, which replaces whoever the server session held.
    if (user) await auth.logout();
    // eslint-disable-next-line func-names
    auth.login({ login: email, password, recaptcha }, function () {});
  }

  const {
    register,
    setError,
    handleSubmit,
    setValue,
    clearErrors,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
      email: '',
      repeatEmail: '',
      password: '',
      passwordRepeat: '',
      recaptcha: '',
    },
    mode: 'onBlur',
  });

  const handleRecaptchaChange = useCallback(
    (token: string | null) => {
      setValue('recaptcha', token ?? '');
      clearErrors('recaptcha');
    },
    [setValue, clearErrors]
  );

  const handleRecaptchaExpired = useCallback(() => {
    setValue('recaptcha', '');
    setError('recaptcha', { type: 'required' });
  }, [setValue, setError]);

  const handleRecaptchaError = useCallback(() => {
    setValue('recaptcha', '');
    if (typeof window !== 'undefined' && (window as any).captchaReset) {
      (window as any).captchaReset();
    }
  }, [setValue]);

  const [isSsoAuthenticating, setIsSsoAuthenticating] = useState(false);
  const handleSsoStart = useCallback(() => {
    setIsSsoAuthenticating(true);
  }, []);
  const handleSsoSuccess = useCallback(() => {
    setIsSsoAuthenticating(false);
  }, []);
  const handleSsoError = useCallback((message: string) => {
    setPopUp({ show: true, message, state: 'error' });
    setIsSsoAuthenticating(false);
  }, []);

  const onSubmit = async (data: FormData) => {
    setIsLoading(true);
    const utm = getUtmData();

    const body: CreateUserTrialParams = {
      name: data.name,
      email: data.email,
      password: data.password,
      recaptcha: data.recaptcha,
      telegram: null,
      password_repeat: data.passwordRepeat,
      ref: '',
      // `custom` IS the trial config id — the endpoint's name for it. Both ids have to reach the API
      // or the account is created with no trial at all, and it still answers 201.
      custom: trialConfigId,
      trialPageId,
      ...utm,
    };

    auth.createTrial(
      {
        ...body,
      },
      (error) => {
        setValue('recaptcha', '');
        if (typeof window !== 'undefined' && (window as any).captchaReset) {
          (window as any).captchaReset();
        }
        setPopUp({
          show: true,
          message: t(apiErrorMessageKey(error, { fallbackKey: 'form.errors.message_error_default_create_account', statusKeys: TRIAL_STATUS_KEYS })),
          state: 'error',
        });
        setIsLoading((prevState) => !prevState);
      },
      () => {
        handleLogin({ email: data.email, password: data.password, recaptcha: data.recaptcha });
        sendGTMEvent({ event: 'createAccount' });
        sessionStorage.setItem('customTrialApproved', 'true');
        router.push('/thanks-for-trial-no-card');
      },
      false
    );
  };

  // There is no API to attach a custom trial to an already-existing account (see
  // src/components/trial/trial-layout-switcher.tsx), so a logged-in visitor should not see a
  // "create a new account" form. SignUpCustomTrial is only used by custom-trial LPs.
  if (user) {
    return (
      <div className={`flex w-full max-w-[557px] flex-col items-center gap-2 ${className}`}>
        <AlreadyLoggedInNotice trialConfigId={trialConfigId} trialPageId={trialPageId} />
      </div>
    );
  }

  return (
    <div className={`relative flex w-full flex-col items-center gap-2 ${className}`}>
      <AuthLoadingOverlay isActive={isSsoAuthenticating} />
      {popUp.show && <Alert message={popUp.message} state={popUp.state} onClose={() => setPopUp({ ...popUp, show: false })} />}

      {showLogo && (
        <div className='mb-10 w-full max-w-[557px]'>
          <Image src='/assets/images/trial/new/logo-np.webp' alt='Logo NoPing' width={150} height={35} />
        </div>
      )}

      <div className='w-full max-w-[557px]'>
        <SsoProviders onAuthStart={handleSsoStart} onAuthSuccess={handleSsoSuccess} onAuthError={handleSsoError} trialContext={{ custom: trialConfigId, trialPageId }} />
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className='flex w-full max-w-[557px] flex-col items-center justify-center gap-3 rounded-[50px] border-4 border-[#424A5B] bg-[#1D2028F2] px-10 py-4'>
        <h2 className='text-center text-3xl font-bold uppercase text-white'>
          <Trans i18nKey='form.title' ns='components/trial/sign-up-custom-trial' components={{ span: <span className='text-[#93FF18]' /> }}>
            {t('form.title', { count: daysTrial })}
          </Trans>
        </h2>

        <div className='flex w-full flex-col items-start justify-start gap-2'>
          <label className='text-xl font-normal uppercase text-white' htmlFor='name'>
            {t('form.label.name')}
          </label>
          <div className='flex w-full flex-col gap-0.5'>
            <input {...register('name')} dir='auto' autoComplete='name' type='text' placeholder='' className='w-full rounded-lg border border-[#424A5B] bg-[#1C1E24] px-4 py-2 font-medium text-white' maxLength={255} />
            {errors.name && <span className='ms-1 flex items-center text-xs font-medium tracking-wide text-red-500'>{errors?.name?.message}</span>}
          </div>
        </div>

        <div className='flex w-full flex-col items-start justify-start gap-2'>
          <label className='text-xl font-normal uppercase text-white' htmlFor='e-mail'>
            {t('form.label.email')}
          </label>
          <div className='flex w-full flex-col gap-0.5'>
            <input {...register('email')} type='text' placeholder='' className='w-full rounded-lg border border-[#424A5B] bg-[#1C1E24] px-4 py-2 font-medium text-white' maxLength={255} />
            {errors.email && <span className='ms-1 flex items-center text-xs font-medium tracking-wide text-red-500'>{errors?.email?.message}</span>}
          </div>
        </div>

        <div className='flex w-full flex-col items-start justify-start gap-2'>
          <label className='text-xl font-normal uppercase text-white' htmlFor='confirm_email'>
            {t('form.label.confirm_email')}
          </label>
          <div className='flex w-full flex-col gap-0.5'>
            <input {...register('repeatEmail')} type='text' placeholder='' className='w-full rounded-lg border border-[#424A5B] bg-[#1C1E24] px-4 py-2 font-medium text-white' maxLength={255} />
            {errors.repeatEmail && <span className='ms-1 flex items-center text-xs font-medium tracking-wide text-red-500'>{errors?.repeatEmail?.message}</span>}
          </div>
        </div>

        <div className='flex w-full flex-col items-start justify-start gap-2'>
          <label className='text-xl font-normal uppercase text-white' htmlFor='password'>
            {t('form.label.password')}
          </label>
          <div className='flex w-full flex-col gap-0.5'>
            <input {...register('password')} type='password' placeholder='' className='w-full rounded-lg border border-[#424A5B] bg-[#1C1E24] px-4 py-2 font-medium text-white' />
            {errors.password && <span className='ms-1 flex items-center text-xs font-medium tracking-wide text-red-500'>{errors?.password?.message}</span>}
          </div>
        </div>

        <div className='flex w-full flex-col items-start justify-start gap-2'>
          <label className='text-xl font-normal uppercase text-white' htmlFor='confirm_password'>
            {t('form.label.confirm_password')}
          </label>
          <div className='flex w-full flex-col gap-0.5'>
            <input {...register('passwordRepeat')} type='password' placeholder='' className='w-full rounded-lg border border-[#424A5B] bg-[#1C1E24] px-4 py-2 font-medium text-white' />
            {errors.passwordRepeat && <span className='ms-1 flex items-center text-xs font-medium tracking-wide text-red-500'>{errors?.passwordRepeat?.message}</span>}
          </div>
        </div>

        <div className='flex w-full flex-col items-start justify-start gap-0.5'>
          <div className='dispaly:inline-block'>
            <CaptchaProvider onChange={handleRecaptchaChange} onExpired={handleRecaptchaExpired} onError={handleRecaptchaError} />
          </div>

          {errors.recaptcha && <span className='ms-1 flex items-center text-xs font-medium tracking-wide text-red-500'>{errors.recaptcha?.message}</span>}
        </div>
        <Button
          type='submit'
          className='mt-4 w-full border-2 border-white py-2.5'
          fullWidth
          variant='primary'
          style={{
            background: 'radial-gradient(50% 50% at 50% 50%, #D4FF27 0%, #AEF55C 100%)',
            boxShadow: '0px 10px 20px 0px #93FF1840',
          }}
          loading={loading}
          disabled={isDisabledButton}
        >
          {t('form.label.button', { count: daysTrial })}
        </Button>
      </form>
    </div>
  );
}

export { SignUpCustomTrial };
